import { useState, useRef } from 'react';
import { useI18n } from '../../contexts/I18nContext';
import storageService from '../../services/storage.service';

interface FileUploadProps {
    onUploadComplete: (fileData: any) => void;
    category?: string;
    accept?: string;
    maxSize?: number; // in bytes
}

export default function FileUpload({
    onUploadComplete,
    category = 'documents',
    accept = '.pdf,.jpg,.jpeg,.png',
    maxSize = 20 * 1024 * 1024 // 20MB default
}: FileUploadProps) {
    const { language } = useI18n();
    const fileInputRef = useRef<HTMLInputElement>(null);

    const [isUploading, setIsUploading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [progress, setProgress] = useState(0);

    const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        // Validate size
        if (file.size > maxSize) {
            setError(language === 'ar'
                ? `حجم الملف يجب أن لا يتجاوز ${maxSize / 1024 / 1024} ميجابايت`
                : `File size must not exceed ${maxSize / 1024 / 1024}MB`);
            return;
        }

        setError(null);
        setIsUploading(true);
        setProgress(10); // Start progress

        try {
            // 1. Get Signed URL
            setProgress(30);
            const signedData = await storageService.getSignedUploadUrl(file);

            // 2. Upload to GCS
            setProgress(60);
            await storageService.uploadFileToGCS(signedData.upload_url, file);

            // 3. Persist Metadata
            setProgress(90);
            const persistedFile = await storageService.persistFile({
                object_name: signedData.object_name,
                public_url: signedData.public_url,
                file_name: file.name,
                mime_type: file.type,
                file_size: file.size,
                category: category
            });

            setProgress(100);
            onUploadComplete(persistedFile);
        } catch (err) {
            console.error('Upload failed:', err);
            setError(language === 'ar' ? 'فشل رفع الملف. يرجى المحاولة مرة أخرى.' : 'Upload failed. Please try again.');
        } finally {
            setIsUploading(false);
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    };

    return (
        <div className="w-full">
            <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileSelect}
                accept={accept}
                className="hidden"
            />

            {isUploading ? (
                <div className="flex items-center gap-3 px-4 py-2 bg-gray-50 rounded-xl border border-gray-100">
                    <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div
                            className="h-full bg-[#5B3FD0] transition-all duration-300"
                            style={{ width: `${progress}%` }}
                        />
                    </div>
                    <span className="text-xs font-bold text-[#5B3FD0]">{progress}%</span>
                </div>
            ) : (
                <button
                    onClick={() => fileInputRef.current?.click()}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-white border border-dashed border-gray-300 hover:border-[#5B3FD0] hover:bg-[#5B3FD0]/5 text-[#707070] hover:text-[#5B3FD0] rounded-xl transition-all group"
                >
                    <svg className="w-5 h-5 transition-transform group-hover:-translate-y-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                    </svg>
                    <span className="text-sm font-bold">
                        {language === 'ar' ? 'رفع ملف' : 'Upload File'}
                    </span>
                </button>
            )}

            {error && (
                <p className="mt-2 text-xs text-red-500 font-medium">
                    {error}
                </p>
            )}
        </div>
    );
}
